import { Link } from "react-router-dom";
import type { BlogPost } from "../../../../mocks/blog";

const categoryColors: Record<string, string> = {
  "Product Sourcing": "bg-amber-50 text-amber-700",
  "Wholesale": "bg-[#D9E2EC] text-[#3A506B]",
  "Inventory": "bg-teal-50 text-teal-700",
  "Logistics": "bg-slate-100 text-slate-600",
  "Supply Chain": "bg-[#C9A227]/10 text-[#C9A227]",
};

interface RelatedPostsProps {
  posts: BlogPost[];
}

export default function RelatedPosts({ posts }: RelatedPostsProps) {
  if (posts.length === 0) return null;

  return (
    <section className="bg-[#F8F8F8] py-16">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="flex items-center justify-between mb-8">
          <div>
            <p className="text-xs font-bold uppercase tracking-widest text-[#C9A227] mb-1">Keep Reading</p>
            <h2 className="text-2xl font-extrabold text-[#222831]">Related Articles</h2>
          </div>
          <Link
            to="/blog"
            className="text-sm font-semibold text-[#3A506B] hover:text-[#222831] flex items-center gap-1.5 transition-colors cursor-pointer whitespace-nowrap"
          >
            All Articles <i className="ri-arrow-right-line text-base"></i>
          </Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {posts.map((post) => {
            const catStyle = categoryColors[post.category] ?? "bg-gray-100 text-gray-600";
            return (
              <Link
                key={post.id}
                to={`/blog/${post.slug}`}
                className="group flex flex-col rounded-2xl overflow-hidden border border-[#E8ECF0] bg-white hover:-translate-y-1 transition-transform duration-300 cursor-pointer"
              >
                <div className="w-full h-44 overflow-hidden">
                  <img
                    src={post.coverImage}
                    alt={post.title}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-5 flex flex-col flex-1">
                  <span className={`self-start text-xs font-bold uppercase tracking-widest px-3 py-1 rounded-full mb-3 ${catStyle}`}>
                    {post.category}
                  </span>
                  <h3 className="text-sm font-bold text-[#222831] leading-snug group-hover:text-[#C9A227] transition-colors line-clamp-2 flex-1">
                    {post.title}
                  </h3>
                  <div className="mt-4 flex items-center gap-2 text-xs text-[#3A506B]/50">
                    <div className={`w-6 h-6 flex items-center justify-center rounded-full text-white text-xs font-bold flex-shrink-0 ${post.author.bgColor}`}>
                      {post.author.initials}
                    </div>
                    <span>{post.author.name}</span>
                    <span>·</span>
                    <span className="flex items-center gap-0.5">
                      <i className="ri-time-line text-xs"></i>
                      {post.readTime} min
                    </span>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
}